// src/api/inboxApi.js
import axios from "axios";
import {
  getToken,
  getActiveOrgId,
  setOrgIdInStorage,
} from "../services/session.js";
import { computeOrgId, orgHeaders } from "./orgHeader.js";
import {
  getOrg as getAdminOrgBase,
  createOrg as createAdminOrgBase,
  updateOrg as updateAdminOrgBase,
  deleteOrg as deleteAdminOrgBase,
} from "./admin/orgsApi";

// =========================
// Base URL
// =========================
// A base mora em ./baseDaApi (uma fonte só para todos os clientes).
import { API_BASE_URL } from "./baseDaApi";
export { API_BASE_URL };

/** Junta path com a base, evitando /api/api/... */
export function joinApi(path) {
  let p = String(path || "");
  if (/^https?:\/\//i.test(p)) return p; // já é absoluto

  if (!p.startsWith("/")) p = `/${p}`;

  // Se a base termina com /api e o path começa com /api, tiramos o /api do path
  if (API_BASE_URL.endsWith("/api") && p.startsWith("/api/")) {
    p = p.slice(4); // remove o prefixo '/api'
  }
  return `${API_BASE_URL}${p}`;
}

export function getWebsocketUrl() {
  const baseUrl = (API_BASE_URL || '').replace(/\/api\/?$/, '');
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) return '/socket.io';
  
  // Se origin já for uma URL completa, usa ela. Senão, constrói a URL.
  if (origin.startsWith('http://') || origin.startsWith('https://')) {
    const url = new URL(origin);
    return `${url.origin}/socket.io`;
  } else {
    // Fallback para desenvolvimento
    return `${window.location.origin}/socket.io`;
  }
}

try {
  if (typeof window !== "undefined") window.__API_BASE_URL__ = API_BASE_URL;
} catch {}

// =========================
// Axios client
// =========================
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});
// Após criarmos o client com baseURL, zeramos para sempre enviar URL completa com joinApi()
api.defaults.baseURL = "";

// Métodos que recebem body vs. sem body
const HTTP_METHODS_WITH_DATA = new Set(["post", "put", "patch"]);
const HTTP_METHODS_WITHOUT_DATA = new Set(["get", "delete", "head", "options"]);

function wrapRequestMethod(method) {
  const original = api[method].bind(api);
  if (HTTP_METHODS_WITH_DATA.has(method)) {
    return (path, data, config) => {
      const url = typeof path === "string" ? joinApi(path) : path;
      return original(url, data, config);
    };
  }
  if (HTTP_METHODS_WITHOUT_DATA.has(method)) {
    return (path, config) => {
      const url = typeof path === "string" ? joinApi(path) : path;
      return original(url, config);
    };
  }
  return (...args) => original(...args);
}

// Envolve os métodos padrão do axios
[...HTTP_METHODS_WITH_DATA, ...HTTP_METHODS_WITHOUT_DATA].forEach((method) => {
  if (typeof api[method] === "function") {
    api[method] = wrapRequestMethod(method);
  }
});

// =========================
// Auth token helper (ÚNICO)
// =========================
export function getAuthToken() {
  // 1) localStorage
  try {
    const stored = localStorage.getItem("token");
    if (stored) return stored;
  } catch {}

  // 2) cookie 'access_token'
  try {
    const match =
      typeof document !== "undefined"
        ? document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/)
        : null;
    if (match) return decodeURIComponent(match[1]);
  } catch {}

  // 3) fallback (ex.: mantido em services/session.js)
  try {
    const fallback = getToken();
    if (fallback) return fallback;
  } catch {}

  return null;
}

export { api };
export const client = api; // alias padrão
export default api;

export function getConversations({ status = "open", limit = 200, channel } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (limit) params.set("limit", String(limit));
  if (channel) params.set("channel", channel);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";
  return api.get(`/inbox/conversations${suffix}`);
}

// =========================
// Inbox bridge helpers
// =========================
let rawWaBridgeFlag;
if (typeof rawWaBridgeFlag === "undefined" && typeof process !== "undefined" && process && process.env) {
  rawWaBridgeFlag = process.env.VITE_INBOX_WA_BRIDGE ?? process.env.REACT_APP_INBOX_WA_BRIDGE;
}

let rawWaProviderFlag;
if (typeof rawWaProviderFlag === "undefined" && typeof process !== "undefined" && process && process.env) {
  rawWaProviderFlag = process.env.VITE_INBOX_WA_PROVIDER ?? process.env.REACT_APP_INBOX_WA_PROVIDER;
}

export const USE_WA_BRIDGE = String(rawWaBridgeFlag ?? "") === "1";
export const USE_WA_PROVIDER = String(rawWaProviderFlag ?? "") === "1";

function normalizeProviderTimestamp(value) {
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

async function fetchProviderChats(limit = 50) {
  const { data } = await api.get(`/integrations/providers/whatsapp_session/chats`, { params: { limit } });
  const src =
    Array.isArray(data?.chats) ? data.chats :
    Array.isArray(data?.items) ? data.items : [];
  return src
    .filter((c) => {
      const jid = String(c?.remote_jid ?? c?.id ?? "");
      return !jid.toLowerCase().endsWith("@lid");
    })
    .map((c) => {
      const rawLast =
        c?.last_message_at ?? c?.lastMessageTimestamp ?? c?.last_message_at;
      return {
        id: String(c?.id ?? ""),
        channel: "whatsapp",
        title: c?.name || c?.id || "Cliente",
        unread: Number(c?.unread ?? 0) || 0,
        last_message_at: normalizeProviderTimestamp(rawLast),
      };
    });
}

async function fetchProviderMessages(conversationId, limit = 50, cursor) {
  const params = { jid: conversationId, limit };
  if (cursor != null) params.cursor = cursor;
  const { data } = await api.get(`/integrations/providers/whatsapp_session/messages`, { params });
  const src = Array.isArray(data?.messages) ? data.messages : [];
  return {
    nextCursor: data?.nextCursor ?? null,
    messages: src.map((m) => ({
      id: String(m?.id ?? `${conversationId || "msg"}-${Math.random()}`),
      conversation_id: String(conversationId ?? m?.conversation_id ?? ""),
      direction: m?.fromMe ? "out" : "in",
      text: typeof m?.text === "string" ? m.text : m?.body ?? null,
      timestamp: m?.timestamp ?? null,
      created_at: normalizeProviderTimestamp(m?.created_at ?? m?.timestamp ?? null),
    })),
  };
}

async function fetchBridgeConversations(limit = 50) {
  const { data } = await api.get(`/inbox/bridge/conversations`, { params: { limit } });
  const convs = Array.isArray(data?.conversations) ? data.conversations : [];
  return convs.map((c) => ({
    id: String(c?.id ?? ""),
    channel: c?.channel || "whatsapp",
    title: c?.title || c?.name || c?.id || "Cliente",
    unread: Number(c?.unread ?? 0) || 0,
    last_message_at: c?.last_message_at ?? c?.lastMessageTimestamp ?? null,
  }));
}

async function fetchBridgeMessages(conversationId, limit = 50, cursor) {
  const params = { conversationId, limit };
  if (cursor != null) params.cursor = cursor;
  const { data } = await api.get(`/inbox/bridge/messages`, { params });
  const messages = Array.isArray(data?.messages) ? data.messages : [];
  return { messages, nextCursor: data?.nextCursor ?? null };
}

export async function fetchConversations(options = {}) {
  const limit = Number.isFinite(options?.limit) ? Number(options.limit) : 200;

  const fetchDb = async () => {
    const params = { ...(options || {}) };
    params.limit = limit;
    const r = await api.get(`/inbox/conversations`, { params });
    if (Array.isArray(r?.data?.items)) return r.data.items;
    if (Array.isArray(r?.data?.conversations)) return r.data.conversations;
    if (Array.isArray(r?.data)) return r.data;
    return [];
  };

  try {
    if (USE_WA_PROVIDER) return await fetchProviderChats(limit);
    if (USE_WA_BRIDGE) return await fetchBridgeConversations(limit);

    // Default mode: ALWAYS from DB. Backfill happens server-side when DB is empty.
    return await fetchDb();
  } catch {
    // If DB fails, only fallback when explicitly enabled.
    try { if (USE_WA_PROVIDER) return await fetchProviderChats(limit); } catch {}
    try { if (USE_WA_BRIDGE) return await fetchBridgeConversations(limit); } catch {}
    return [];
  }
}

export async function fetchMessages(conversationId, options = {}) {
  const limit = Number.isFinite(options?.limit) ? Number(options.limit) : 200;
  const cursor = options?.cursor;

  const fetchDb = async () => {
    const params = { limit };
    if (cursor != null) params.cursor = cursor;
    const url = `/inbox/conversations/${conversationId}/messages`;
    const { data } = await api.get(url, { params });
    const messages = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.messages)
        ? data.messages
        : Array.isArray(data)
          ? data
          : [];
    return { messages, nextCursor: data?.nextCursor ?? null };
  };

  try {
    if (USE_WA_PROVIDER) return await fetchProviderMessages(conversationId, limit, cursor);
    if (USE_WA_BRIDGE) return await fetchBridgeMessages(conversationId, limit, cursor);

    // Default mode: ALWAYS from DB. Backfill happens server-side when DB is empty.
    return await fetchDb();
  } catch {
    try { if (USE_WA_PROVIDER) return await fetchProviderMessages(conversationId, limit, cursor); } catch {}
    try { if (USE_WA_BRIDGE) return await fetchBridgeMessages(conversationId, limit, cursor); } catch {}
    return { messages: [], nextCursor: null };
  }
}

// =========================
// Inbox-related APIs
// =========================

// Alerta de atendimento (28/09/2026): o canal SSE `/inbox/alerts/stream` era um esboço que nunca
// mandava alerta, e `getInboxAlerts`/`getAlertsStreamUrl` só serviam a ele. O aviso agora vem
// pelo socket ('inbox:handoff') e a lista por GET /inbox/alerts — ver
// components/inbox/AlertaDeAtendimento.jsx.

export async function getAiSettings() {
    const { data } = await api.get('/ai/settings');
    return data;
}

/**
 * Endereço de arquivo servido ESTATICAMENTE (fora de /api).
 *
 * `joinApi` prefixa a base da API e colaria um /api indevido em /uploads/content,
 * que é montado na raiz — de propósito, para <img> em e-mail e WhatsApp funcionar
 * sem token (o nome do arquivo é UUID, não enumerável).
 */
export function getStaticUrl(caminho) {
  if (!caminho) return null;
  const p = String(caminho);
  if (/^https?:\/\//i.test(p)) return p;
  const origem = (API_BASE_URL || '').replace(/\/api\/?$/, '');
  return `${origem}${p.startsWith('/') ? '' : '/'}${p}`;
}

export function getAssetUrl(assetId) {
    if (!assetId) return null;
    const url = joinApi(`/assets/${assetId}`);
    const token = getAuthToken();
    return token ? `${url}?token=${token}` : url;
}

export function getMediaUrl(urlPath) {
    if (!urlPath) return null;
    const s = String(urlPath);
    if (/^https?:\/\//i.test(s)) return s;
    const url = joinApi(s);
    const token = getAuthToken();
    if (!token) return url;
    return url.includes('?') ? `${url}&token=${token}` : `${url}?token=${token}`;
}

export async function getProfessionals(orgId) {
  const { data } = await api.get(`/professionals`, { params: { org_id: orgId } });
  return data;
}


/**
 * Agendamentos de um contato.
 * @param {object} opts
 * @param {string} opts.from            ISO — só agendamentos a partir desta data (start_at >=).
 * @param {boolean} opts.includeCancelled  inclui os cancelados (o backend os esconde por padrão).
 */
export async function getAppointments(orgId, contactId, opts = {}) {
    const params = { org_id: orgId, contact_id: contactId };
    if (opts.from) params.start_at = opts.from;
    if (opts.includeCancelled) params.include_cancelled = 1;
    const { data } = await api.get(`/appointments`, { params });
    return data;
}

export async function getMetaAccounts(channel) {
    const { data } = await api.get('/channels/meta/accounts', { params: { channel } });
    return data;
}

// =========================
// Utils de moeda
// =========================
export function parseBRLToCents(input) {
  if (typeof input === "number") {
    return Number.isFinite(input) ? Math.round(input * 100) : NaN;
  }
  if (typeof input !== "string") return NaN;
  const norm = input
    .replace(/\s/g, "")
    .replace(/^R\$/i, "")
    .replace(/\./g, "")
    .replace(",", ".");
  if (!norm) return 0;
  const num = Number(norm);
  return Number.isFinite(num) ? Math.round(num * 100) : NaN;
}

export function centsToBRL(cents = 0, currency = "BRL") {
  const value = Number.isFinite(cents) ? cents / 100 : 0;
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);
  } catch {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  }
}

// =========================
// Helpers de headers
// =========================
/** Remove prefixo '/api' (se houver) para classificar a rota */
function stripApiPrefix(p) {
  return p.startsWith("/api/") ? p.slice(4) : p;
}

function isPublicPath(path = "") {
  const raw = path.startsWith("/") ? path : `/${path}`;
  const p = stripApiPrefix(raw);

  const isPublicAuthPath =
    p === "/auth/login" ||
    p === "/auth/register" ||
    p === "/auth/register/confirmar" ||
    p === "/auth/register/reenviar" ||
    p === "/auth/forgot-password" ||
    p === "/auth/reset-password" ||
    p === "/auth/refresh";

  return (
    isPublicAuthPath ||
    p.startsWith("/public/") ||
    // Só os RECEPTORES de webhook (/webhooks/meta, /webhooks/whatsapp) são públicos.
    // Sem a barra, o prefixo casava com /webhooks-outbound (API de gestão, autenticada)
    // e o interceptor removia o Authorization → a tela de Webhooks dava 401.
    p === "/webhooks" ||
    p.startsWith("/webhooks/") ||
    p === "/health" ||
    p === "/ping"
  );
}

function ensureAuthorization(headers) {
  const token = getAuthToken();
  if (!token) {
    delete headers.Authorization;
    delete headers.authorization;
    return;
  }
  if (!headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  } else if (headers.authorization && !headers.Authorization) {
    headers.Authorization = headers.authorization;
    delete headers.authorization;
  }
}

function resolveOrgIdForRequest() {
  try {
    // 1) computeOrgId (pode ler token e storage)
    // 2) getActiveOrgId (storage)
    // 3) activeOrg.id salvo como JSON
    const computed = computeOrgId();
    if (computed) return computed;

    const act = getActiveOrgId();
    if (act) return String(act);

    const raw = localStorage.getItem("activeOrg");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id) return String(parsed.id);
    }
  } catch {}
  return null;
}

function ensureOrgHeader(headers, path, meta) {
  const globalScope = meta?.scope === "global";
  if (globalScope || isPublicPath(path) || meta?.noOrgHeader) {
    delete headers["X-Org-Id"];
    delete headers["x-org-id"];
    return;
  }
  let applied = false;
  try {
    const oh = orgHeaders();
    if (oh && typeof oh === "object") {
      Object.entries(oh).forEach(([key, value]) => {
        if (value != null && value !== "") {
          headers[key] = value;
          applied = true;
        }
      });
    }
  } catch {}
  if (applied) return;

  const orgId = resolveOrgIdForRequest();
  if (orgId) {
    headers["X-Org-Id"] = orgId;
  } else {
    delete headers["X-Org-Id"];
    delete headers["x-org-id"];
  }
}

// Boot do header X-Org-Id (útil para libs que leem defaults)
try {
  const savedOrg = getActiveOrgId();
  if (savedOrg) {
    api.defaults.headers.common["X-Org-Id"] = String(savedOrg);
  } else {
    delete api.defaults.headers.common["X-Org-Id"];
  }
} catch {}

// =========================
// Interceptors
// =========================
api.interceptors.request.use((config) => {
  const cfg = config || {};
  const path =
    typeof cfg.url === "string"
      ? new URL(cfg.url, "http://local").pathname
      : "/";

  cfg.headers = cfg.headers || {};

  // 1) Auth
  if (!cfg.meta?.noAuth && !isPublicPath(path)) {
    ensureAuthorization(cfg.headers);
  } else {
    delete cfg.headers.Authorization;
    delete cfg.headers.authorization;
  }

  // 2) Org
  ensureOrgHeader(cfg.headers, path, cfg.meta || {});

  return cfg;
});

/**
 * Páginas abertas a quem não tem sessão — as mesmas rotas públicas do App.jsx.
 * Um 401 aqui não é motivo para mandar ninguém ao login (ver o interceptador abaixo).
 */
const PAGINAS_PUBLICAS = [
  /^\/$/, /^\/login\/?$/, /^\/register\/?$/, /^\/forgot-password\/?$/,
  /^\/reset-password\/?$/, /^\/privacidade\/?$/, /^\/invite\/[^/]+\/?$/,
];
export function ehPaginaPublica(pathname) {
  const caminho = String(pathname || "/");
  return PAGINAS_PUBLICAS.some((re) => re.test(caminho));
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Normalize errorHandler format: { error: { code, message, request_id } } → { error: "CODE", message: "..." }
    const data = err?.response?.data;
    if (data && typeof data.error === "object" && data.error !== null && data.error.code) {
      err.response.data = {
        ...data,
        error: data.error.code,
        message: data.error.message || data.error.code,
        request_id: data.error.request_id || undefined,
      };
    }

    const status = err?.response?.status;
    // Tratativa clara para falta de escopo de organização
    const errCode = err?.response?.data?.error;
    if (status === 400 && (errCode === "org_required" || errCode === "ORG_ID_REQUIRED")) {
      try {
        if (typeof window !== "undefined") {
          console.warn("[Inbox] Organização requerida. Selecione uma organização.");
          window.dispatchEvent(new CustomEvent("inbox:org_required"));
          // Opcional: navegar para uma tela de seleção, se existir
          // window.location.href = "/orgs/select";
        }
      } catch {}
    }

    if (status === 401) {
      try {
        localStorage.removeItem("token");
      } catch {}
      try {
        if (typeof document !== "undefined") {
          document.cookie = "access_token=; Max-Age=0; path=/";
        }
      } catch {}
      // ⚠️ SÓ LEVA AO LOGIN QUEM ESTAVA NUMA PÁGINA QUE EXIGE SESSÃO.
      //
      // Antes isto redirecionava a partir de QUALQUER página. Com um token vencido
      // guardado no navegador, os provedores globais chamam /api/orgs e
      // /api/auth/memberships até na landing; a API devolve 401 e o visitante era
      // arrancado para /login. Quem já tinha usado o app nunca mais via a landing,
      // o cadastro ou o convite — nem a /privacidade, que o revisor da Meta precisa
      // abrir sem sessão. Medido no navegador em 24/09/2026.
      //
      // Numa página pública, o 401 só quer dizer "a sessão guardada morreu": o
      // token é descartado (acima) e a pessoa fica onde está.
      if (
        typeof window !== "undefined" &&
        !ehPaginaPublica(window.location?.pathname)
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// =========================
// Token helpers
// =========================
export function setAuthToken(token) {
  try {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
    delete api.defaults.headers.common.Authorization;
    delete axios.defaults.headers?.common?.Authorization;
  } catch {}
}

export function clearAuthToken() {
  setAuthToken(null);
}

// =========================
// Org helpers
// =========================
export function setActiveOrg(id) {
  try {
    setOrgIdInStorage(id);
    if (id) api.defaults.headers.common["X-Org-Id"] = String(id);
    else delete api.defaults.headers.common["X-Org-Id"];
  } catch {}
}

export function getImpersonateOrgId() {
  try {
    return localStorage.getItem("impersonate.orgId") || "";
  } catch {
    return "";
  }
}
export function setImpersonateOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem("impersonate.orgId", orgId);
    else localStorage.removeItem("impersonate.orgId");
  } catch {}
}

// Mantém este helper para rotas PÚBLICAS/ADMIN onde **não** queremos X-Org-Id.
function withGlobalScope(options = {}) {
  const next = { ...(options || {}) };
  next.meta = { ...(options?.meta || {}) };
  if (!next.meta.scope) next.meta.scope = "global";
  return next;
}

// =========================
// Orgs API (admin e atual)
// =========================
export { listOrgs as adminListOrgs } from "./admin/orgsApi";
export { setOrgBaileysFeature } from "./admin/orgsApi";

export async function listAdminOrgs(status = "active", options = {}) {
  const params = { status };
  const q =
    options?.params?.q ?? options?.params?.search ??
    options?.q ?? options?.search ?? "";
  if (q) params.q = String(q).trim();
  const extras = { ...(options?.params || {}) };
  delete extras.q; delete extras.search; delete extras.status;
  Object.entries(extras).forEach(([k, v]) => { if (v != null) params[k] = v; });
  // Use /orgs directly (no org_id required) instead of /admin/orgs (which doesn't exist)
  const { data } = await api.get("/orgs", { params, meta: { scope: "global" } });
  return data;
}

export async function patchAdminOrg(orgId, payload, _options = {}) {
  return updateAdminOrgBase(orgId, payload);
}
export async function getAdminOrg(orgId, _options = {}) {
  if (!orgId) return null;
  return getAdminOrgBase(orgId);
}
export async function postAdminOrg(payload, _options = {}) {
  return createAdminOrgBase(payload);
}
export async function deleteAdminOrg(id, _options = {}) {
  return deleteAdminOrgBase(id);
}

/** ✅ org corrente (NÃO usa withGlobalScope; mantém X-Org-Id) */
export async function getCurrentOrg(options = {}) {
  const { data } = await api.get("/orgs/current", options);
  return data;
}

// orgs do usuário (formato compat)
export async function getMyOrgs() {
  const { data } = await api.get("/orgs");
  if (data && typeof data === "object" && Array.isArray(data.data)) {
    return { items: data.data, data: data.data };
  }
  return data;
}

// seleciona org ativa (back pode validar; front salva via setActiveOrg)
export async function switchOrg(orgId) {
  await api.post('/orgs/select', { orgId }, { meta: { noOrgHeader: true } });
  setActiveOrg(orgId);
}

// =========================
// Utils de busca externa (públicas)
// =========================
export async function lookupCNPJ(cnpj) {
  const { data } = await api.get(`/utils/cnpj/${encodeURIComponent(cnpj)}`, withGlobalScope());
  return data;
}
export async function lookupCEP(cep) {
  const { data } = await api.get(`/utils/cep/${encodeURIComponent(cep)}`, withGlobalScope());
  return data;
}

// =========================
// Plans/Admin (escopo global)
// =========================
export async function getPlanCredits(planId) {
  const { data } = await api.get(`/admin/plans/${planId}/credits`, withGlobalScope());
  return data?.data ?? [];
}
export async function setPlanCredits(planId, payload) {
  const { data } = await api.put(`/admin/plans/${planId}/credits`, payload, withGlobalScope());
  return data?.data ?? [];
}
export async function putAdminOrgPlan(orgId, payload, options = {}) {
  return api.put(`/admin/orgs/${orgId}/plan`, payload, withGlobalScope(options));
}
// ⚠️ REMOVIDOS: `patchAdminOrgCredits` (PATCH /admin/orgs/:id/credits) e
// `getOrgPlanSummary` (GET /orgs/:id/plan/summary).
//
// Nenhuma tela chamava os dois, e nenhum dos dois endereços existe no backend — conferido
// por HTTP, com o verbo de cada um. Eram ajudantes escritos para telas que não foram feitas.
// Créditos por organização se ajustam por `/admin/plans/:id/credits`, que existe e é usado.
export async function listAdminPlans(options = {}) {
  return api.get(`/admin/plans`, withGlobalScope(options));
}
export async function adminListPlans(options = {}) {
  const { data: resp, status } = await client.get("/admin/plans", withGlobalScope(options));
  if (status !== 200) throw new Error(`admin/plans ${status}`);

  if (resp?.data && typeof resp.data === "object") {
    const plans = Array.isArray(resp.data.plans) ? resp.data.plans : [];
    const feature_defs = Array.isArray(resp.data.feature_defs) ? resp.data.feature_defs : [];
    const plan_features = Array.isArray(resp.data.plan_features) ? resp.data.plan_features : [];
    return { plans, feature_defs, plan_features };
  }
  if (resp && Array.isArray(resp.plans)) {
    return {
      plans: resp.plans,
      feature_defs: Array.isArray(resp.feature_defs) ? resp.feature_defs : [],
      plan_features: Array.isArray(resp.plan_features) ? resp.plan_features : [],
    };
  }
  if (resp && Array.isArray(resp.data)) {
    return {
      plans: resp.data,
      feature_defs: Array.isArray(resp.meta?.feature_defs) ? resp.meta.feature_defs : [],
      plan_features: Array.isArray(resp.meta?.plan_features) ? resp.meta.plan_features : [],
    };
  }
  if (Array.isArray(resp)) {
    return { plans: resp, feature_defs: [], plan_features: [] };
  }
  const keys = resp && typeof resp === "object" ? Object.keys(resp) : [];
  throw new Error(`admin/plans payload inválido - got: ${keys.join(",")}`);
}
export async function adminCreatePlan(payload, options = {}) {
  return api.post(`/admin/plans`, payload, withGlobalScope(options));
}
export async function adminUpdatePlan(planId, payload, options = {}) {
  return api.patch(`/admin/plans/${planId}`, payload, withGlobalScope(options));
}
export async function adminDuplicatePlan(planId, options = {}) {
  return api.post(`/admin/plans/${planId}/duplicate`, {}, withGlobalScope(options));
}
export async function adminDeletePlan(planId, options = {}) {
  return api.delete(`/admin/plans/${planId}`, withGlobalScope(options));
}
export async function adminSetPlanStatus(planId, status, options = {}) {
  return api.put(`/admin/plans/${planId}/status`, { status }, withGlobalScope(options));
}
// ⚠️ REMOVIDO: `adminGetPlanFeatures` (GET /admin/plans/:id/features).
//
// A rota saiu do backend de propósito — ela consultava a tabela `plan_features`, apagada na
// migration 0061, e o próprio routes/adminPlans.js explica isso num comentário. As features
// do plano entram por PUT /admin/plans/:id. A tela nunca acompanhou a mudança, e o ajudante
// ficou aqui apontando para o vazio, sem nenhum chamador.
export async function adminGetPlanCredits(planId, options = {}) {
  if (!planId) return [];
  try {
    const r = await api.get(`/admin/plans/${planId}/credits`, withGlobalScope(options));
    return Array.isArray(r.data?.data) ? r.data.data : [];
  } catch (error) {
    if (error?.response?.status === 404) return [];
    throw error;
  }
}
export async function adminUpdatePlanCredits(planId, credits, options = {}) {
  if (!planId) return [];
  const payload = Array.isArray(credits) ? credits : [];
  const r = await api.put(`/admin/plans/${planId}/credits`, { data: payload }, withGlobalScope(options));
  return Array.isArray(r.data?.data) ? r.data.data : [];
}
export async function adminGetPlanCreditsSummary(planId, options = {}) {
  if (!planId) return { plan_id: planId ?? null, credits: [] };
  try {
    const credits = await adminGetPlanCredits(planId, options);
    return { plan_id: planId, credits };
  } catch (error) {
    if (error?.response?.status === 404) return { plan_id: planId, credits: [] };
    throw error;
  }
}
// ⚠️ REMOVIDO junto com `adminGetPlanFeatures`: `adminPutPlanFeatures`
// (PUT /admin/plans/:id/features). Mesma rota morta, mesmo motivo — a tabela `plan_features`
// saiu na migration 0061 e as features do plano entram por PUT /admin/plans/:id. Zero
// chamadores.

// ===== Billing / Histórico =====
export async function adminGetOrgBillingHistory(orgId) {
  const { data } = await api.get(`/admin/orgs/${orgId}/billing/history`, { meta: { scope: "global" } });
  if (!data?.ok) throw new Error(JSON.stringify(data || {}));
  return data.data;
}
export async function getOrgBillingHistory(orgId) {
  const { data } = await api.get(`/orgs/${orgId}/billing/history`);
  if (!data?.ok) throw new Error(JSON.stringify(data || {}));
  return data.data;
}

// ===== Planos (short) =====
export async function adminListPlansShort() {
  const { data: resp } = await api.get(`/admin/plans`, { meta: { scope: "global" } });
  const plans =
    (resp?.data?.plans ?? resp?.plans ?? resp?.data ?? (Array.isArray(resp) ? resp : [])).map((p) => ({
      id: p.id ?? p.plan_id ?? p.uuid,
      name: p.name ?? p.title ?? p.slug ?? "Plano",
      status: p.status ?? null,
    }));
  return plans;
}

// Trocar o plano pede a senha de quem troca (03/10/2026). Senha errada volta 403.
export async function adminPutOrgPlan(orgId, planId, password) {
  return api.put(`/admin/orgs/${orgId}/plan`, { plan_id: planId, password }, { meta: { scope: "global" } });
}

export {
  adminCreatePlan as createPlan,
  adminUpdatePlan as updatePlan,
};

export { setOrgIdHeaderProvider } from "./orgHeader.js";
